/**
 * LiveStreamingChatList — the reversed, scrollable message region of the live
 * chat overlay. Wraps a `VirtualList<CommunityChatMessageEntity>` in `reversed`
 * mode and dispatches each item through `LiveStreamingChatMessageItem`.
 *
 * Flutter source of truth: `messagesList`
 * (`live_streaming_screen.dart:1273-1289`) — a `ListView.builder(reverse: true)`
 * over `_messages` rendering `CommunityLiveMessageItem` per row, newest at the
 * bottom of the viewport.
 *
 * ----------------------------------------------------------------------
 * Index-space note (load-bearing, mirrors `CommunityMessagesList`):
 *
 * The `useLiveStreamingChat` hook returns `messages` **newest-first** (lower
 * index = newer; sorted desc by `createdTimestamp`). `VirtualList(reversed)`
 * expects items in **chronological** order (oldest at index 0, newest at the
 * end) — items render top-to-bottom and the viewport is anchored to the bottom.
 * This list therefore feeds `VirtualList` the array reversed
 * (`[...messages].reverse()`).
 * ----------------------------------------------------------------------
 *
 * Imperative handle (`LiveStreamingChatListHandle`): forwards `scrollToBottom`
 * (snap to the NEWEST message) to the underlying `VirtualList`. In the reversed
 * list the newest message anchors the bottom, so Flutter's "go to newest"
 * (`_listScrollController.animateTo(0, …)`, `live_streaming_screen.dart:805-809`
 * / `:842-847`) is a scroll-to-bottom. The Task-9 overlay imports this type for
 * its list ref (post-send auto-scroll + unread-pill tap).
 */

import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { VirtualList } from '@/presentation/components/common/virtualList';
import type { VirtualListHandle } from '@/presentation/components/common/virtualList';
import type { QaAttrOptions } from '@/presentation/utils/qaAttr';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { LiveStreamingChatMessageItem } from './LiveStreamingChatMessageItem';

/**
 * Imperative handle exposed by `LiveStreamingChatList`. The Task-9 overlay
 * drives `scrollToBottom` from the post-send auto-scroll and the unread-pill
 * tap. In the reversed list, "go to newest" is scroll-to-bottom.
 */
export interface LiveStreamingChatListHandle {
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

export interface LiveStreamingChatListProps {
  /**
   * Optional `data-qa-*` set spread onto the underlying `VirtualList` root so
   * the qa-tester can scope to the list. Defaults to `live-chat-messages` when
   * omitted. Rows carry their own `live-chat-message-<id>` ids.
   */
  qa?: QaAttrOptions;
  /** Newest-first message list from `useLiveStreamingChat` (lower index = newer). */
  messages: CommunityChatMessageEntity[];
  /** Hook callback — top sentinel intersected, load older page. */
  onLoadMore: () => void;
  /** Hook callback — user scrolled to the bottom (newest visible). */
  onScrolledToBottom: () => void;
  /** Hook callback — user scrolled away from the bottom. */
  onScrolledAway: () => void;
  /**
   * Sticky chrome (composer below, unread pill above) overlapping the window
   * viewport — forwarded to `VirtualList`'s `reversedBottomPinInset` so the
   * short-list bottom-pin pads correctly (same contract as
   * `CommunityMessagesList`).
   */
  reversedBottomPinInset?: { top?: number; bottom?: number };
}

export const LiveStreamingChatList = forwardRef<
  LiveStreamingChatListHandle,
  LiveStreamingChatListProps
>(({
  qa,
  messages,
  onLoadMore,
  onScrolledToBottom,
  onScrolledAway,
  reversedBottomPinInset,
}, ref) => {
  const responsive = useResponsiveDesign();

  const virtualListRef = useRef<VirtualListHandle>(null);

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottom: (behavior: ScrollBehavior = 'smooth') => {
        virtualListRef.current?.scrollToBottom(behavior);
      },
    }),
    [],
  );

  // Chronological order for `VirtualList(reversed)` (oldest at index 0, newest
  // at the end). See the index-space note in the file header. Memoized by
  // `messages` identity.
  const chronologicalMessages = useMemo(
    () => [...messages].reverse(),
    [messages],
  );

  // Stable key extractor — `id` is the message document id / optimistic UUID.
  const getItemKey = useCallback(
    (m: CommunityChatMessageEntity) => m.id,
    [],
  );

  const renderItem = useCallback(
    (message: CommunityChatMessageEntity) => (
      <LiveStreamingChatMessageItem message={message} />
    ),
    [],
  );

  return (
    <div
      style={{
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
      }}
    >
      <VirtualList<CommunityChatMessageEntity>
        ref={virtualListRef}
        qa={qa ?? { id: 'live-chat-messages' }}
        items={chronologicalMessages}
        renderItem={renderItem}
        getItemKey={getItemKey}
        reversed
        reversedBottomPinInset={reversedBottomPinInset}
        gap={responsive.spacing.base}
        defaultItemHeight={responsive.community.defaultItemHeight}
        onReachedEnd={onLoadMore}
        onScrolledToBottom={onScrolledToBottom}
        onScrolledAwayFromBottom={onScrolledAway}
        scrolledAwayFromBottomThreshold={responsive.community.scrolledAwayThreshold}
      />
    </div>
  );
});

LiveStreamingChatList.displayName = 'LiveStreamingChatList';
